import { NodeAxios } from '../api'

export interface ScenarioFileManifest {
    file: string
    label: string
    description: string
    covers: string[]
    scenario_count: number
}

export interface Scenario {
    id: string
    label: string
    description: string
    campaign_type: 'product' | 'company' | 'people'
    functional_type: string
    completion_state: 'empty' | 'partial' | 'complete'
    data: Record<string, any>
}

export interface ScenarioFile {
    file: string
    label: string
    description: string
    covers: string[]
    scenarios: Scenario[]
}

export interface LoadScenarioResult {
    campaignId: string
    campaignName: string
    scenario_id: string
    label: string
}

export const scenarioService = {
    /**
     * List all available scenario files (manifests only — no scenarios array).
     */
    async listScenarioFiles(): Promise<{ success: boolean; files: ScenarioFileManifest[] }> {
        const result = await NodeAxios.get('/campaigns/admin/test-scenarios')
        return result.data || result
    },

    /**
     * Get the full scenarios array from a named file.
     */
    async getScenarioFile(file: string): Promise<{ success: boolean } & ScenarioFile> {
        const result = await NodeAxios.get(`/campaigns/admin/test-scenarios/${file}`)
        return result.data || result
    },

    /**
     * Create a single campaign from one scenario.
     */
    async loadScenario(file: string, scenario_id: string): Promise<{ success: boolean } & LoadScenarioResult> {
        const result = await NodeAxios.post('/campaigns/admin/test-scenarios/load', { file, scenario_id })
        return result.data || result
    },

    /**
     * Create campaigns for every scenario in a file.
     */
    async loadAllScenarios(file: string): Promise<{ success: boolean; created: LoadScenarioResult[]; errors: { scenario_id: string; error: string }[] }> {
        const result = await NodeAxios.post('/campaigns/admin/test-scenarios/load-all', { file })
        return result.data || result
    },

    /**
     * Soft-delete all test scenario campaigns for this org.
     */
    async cleanupTestScenarios(): Promise<{ success: boolean; deleted: number }> {
        const result = await NodeAxios.delete('/campaigns/admin/test-scenarios/cleanup')
        return result.data || result
    },

    /**
     * Load scenarios from a custom uploaded JSON (not a server seed file).
     * Iterates the provided array and calls loadScenario for each — no separate endpoint.
     * The caller provides parsed JSON matching the ScenarioFile schema.
     */
    async loadFromUploadedData(fileSlug: string, scenarios: Scenario[]): Promise<{ created: LoadScenarioResult[]; errors: { scenario_id: string; error: string }[] }> {
        const created: LoadScenarioResult[] = []
        const errors: { scenario_id: string; error: string }[] = []
        for (const scenario of scenarios) {
            try {
                const res = await NodeAxios.post('/campaigns/admin/test-scenarios/load', {
                    file: fileSlug,
                    scenario_id: scenario.id,
                })
                const data = res.data || res
                if (data.success) {
                    created.push(data)
                } else {
                    errors.push({ scenario_id: scenario.id, error: data.error ?? 'Unknown error' })
                }
            } catch (err: any) {
                errors.push({ scenario_id: scenario.id, error: err?.message ?? 'Request failed' })
            }
        }
        return { created, errors }
    },
}
